<template lang="pug">
v-card.main-card
	v-layout.align-center.fill-height
		v-menu.events-dropdown.events-dropdown--left(offset-y)
			v-toolbar-title.liondesk.events-dropdown-title(slot='activator')
				span
					v-icon.left-color-icon stop
					v-icon(:class="showComparisonLeftIcon()" :style="{color: 'rgba(0, 94, 162, 0.4)'}") stop
				span.ml-1.text-capitalize {{ selectedDropdownLeft }}
				v-icon(color='primary', right) arrow_drop_down
			v-list.events-dropdown-list
				v-list-tile(
					v-for='event in eventsHeadersDropdownLeft',
					:key='event.value',
					@click='selectedItemEventsDropdownLeft(event)'
				) 
					v-list-tile-title.menu-date-caption
						span
						| {{ event.text }}
		p.dropdown-versus vs
		v-menu.events-dropdown.events-dropdown--right(offset-y)
			v-toolbar-title.liondesk.events-dropdown-title(slot='activator')
				span
					v-icon(:class="selectedDropdownRightColor") stop
					v-icon(:class="showComparisonRightIcon()" :style="{color: 'rgba(247, 136, 62, 0.4)'}") stop
				span.text-capitalize(ref="rightDD" :value="selectedDropdownRight", @input="updateValues()") {{ selectedDropdownRight }}
				v-icon(color='primary', right) arrow_drop_down
			v-list.events-dropdown-list
				v-list-tile(
					v-for='event in eventsHeadersDropdownRight',
					:key='event.value',
					@click='selectedItemEventsDropdownRight(event)'
				) 
					v-list-tile-title
						span
							| {{ event.text }}
				v-list-tile(@click="clearSelectedEventDropdownRight()").dropdown-clear
					v-list-tile-title
						span.dropdown-clear-span
							v-icon.dropdown-clear-icon clear
							| Nothing
		v-spacer.grow
		v-menu.interval-dropdown(offset-y)
			v-toolbar-title.liondesk.interval-dropdown-title(slot='activator')
				span.text-capitalize {{ selectedGraphInterval }}
				v-icon(color='primary', right) arrow_drop_down
			v-list
				v-list-tile(
					v-for='item in graphIntervals',
					:key='item.interval',
					@click='selectedIntervalOnDropdown(item.interval)'
				) 
					v-list-tile-title.menu-date-caption.text-capitalize
						span
						| {{ item.interval }}
	line-chart(
    v-if="!responseIfNoData"
    :headers="headers"
    :graphData="graphData" 
    :selectedDateRange="selectedDateRange"
    :selectedDropdownLeft="selectedDropdownLeft" 
    :selectedDropdownRight="selectedDropdownRight"
    :selectedGraphInterval="selectedGraphInterval"
    :includeComparedPercentage="includeComparedPercentage"
    :isGraphLoading="isGraphLoading"
  )
	v-layout.no-data-container.align-center.justify-center.column(v-if="responseIfNoData")
		v-icon.no-data-icon error 
		p.no-data-text 
			| Your Search Returned 
			span.font-weight-bold
				| No Results
</template>

<script>
import { mapState, mapActions } from 'vuex';
import LineChart from './LineChart';
import {
  TEAM_MEMBER,
  SELECT,
  HOURLY,
  DAILY,
  WEEKLY,
  MONTHLY,
  CURRENT_DAY,
  YESTERDAY,
  THIS_WEEK,
  LAST_7_DAYS,
  LAST_WEEK,
  LAST_14_DAYS,
  THIS_MONTH,
  LAST_30_DAYS,
  LAST_MONTH,
  LAST_90_DAYS,
  THIS_YEAR,
  LAST_7_DAYS_VAL,
  EMAIL_SENT,
} from '@/Reporting/config/constants';

const classes = {
  hideComparisonIcon: 'hide-comparison-icon',
  showComparisonIcon: 'show-comparison-icon',
  rightColorIcon: 'right-color-icon',
  hideColorIcon: 'hide-color-icon',
  leftColorSpan: 'left-color-span',
};

export default {
  name: 'ReportingGraph',
  components: {
    LineChart,
  },
  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    selectedDateRange: {
      type: String,
      default: '',
    },
    selectedDateInterval: {
      type: String,
      default: '',
    },
    includeComparedPercentage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      eventsHeadersDropdownLeft: [],
      eventsHeadersDropdownRight: [],
      selectedDropdownLeft: SELECT,
      selectedDropdownLeftColor: classes.leftColorSpan,
      selectedDropdownRight: SELECT,
      selectedDropdownRightColor: classes.hideColorIcon,
      graphIntervals: [],
      selectedGraphInterval: '',
      graphDateRange: LAST_7_DAYS_VAL,
      graphLeftDropdown: EMAIL_SENT,
      graphRightDropdown: SELECT,
      comparison: false,
      filterSelection: [],
    };
  },
  computed: {
    ...mapState('reportingGraph', [
      'graphData',
      'isGraphLoading',
      'graphError',
      'responseIfNoData',
    ]),
    watchSelections() {
      return [
        this.selectedDateInterval,
        this.includeComparedPercentage,
        this.selectedGraphInterval,
        this.graphLeftDropdown,
        this.graphRightDropdown,
        this.filterSelection,
      ];
    },
  },
  watch: {
    selectedDateRange(value) {
      this.defaultGraphInterval(value);
      this.defineGraphIntervalItems(value);
    },
    watchSelections(newValue, oldValue) {
      if (newValue === oldValue || !newValue.length) return;
      const [dateRange, comparison, interval, leftDropdown, rightDropdown, filter] = newValue;
      this.graphDateRange = dateRange;
      this.graphRightDropdown = rightDropdown;
      this.comparison = comparison;
      this.selectedGraphInterval = interval;
      this.graphLeftDropdown = leftDropdown;
      this.filterSelection = filter;
      this.searchGraphData();
    },
  },
  created() {
    this.eventsHeadersDropdownLeft = this.headers.filter(header => header.value !== TEAM_MEMBER);
    this.defaultGraphInterval(this.selectedDateRange);
    this.defineGraphIntervalItems(this.selectedDateRange);
    this.selectedDropdownLeft = EMAIL_SENT;
    this.setEventsHeadersDropdownRight();
    this.searchGraphData();
  },
  methods: {
    ...mapActions('reportingGraph', ['getInfoForReportingGraph']),
    async searchGraphData() {
      await this.getInfoForReportingGraph({
        dateRange: this.graphDateRange,
        comparison: this.comparison,
        selectedTimeInterval: this.selectedGraphInterval,
        selectedLeftDropdown: this.graphLeftDropdown,
        selectedRightDropdown: this.graphRightDropdown,
        selectedFilter: this.filterSelection,
      });
    },
    selectedIntervalOnDropdown(value) {
      this.selectedGraphInterval = value;
    },
    defaultGraphInterval(selectedDateRange) {
      if (selectedDateRange === CURRENT_DAY || selectedDateRange === YESTERDAY) {
        this.selectedGraphInterval = HOURLY;
      } else if (
        selectedDateRange === THIS_WEEK ||
        selectedDateRange === LAST_7_DAYS ||
        selectedDateRange === LAST_WEEK ||
        selectedDateRange === LAST_14_DAYS
      ) {
        this.selectedGraphInterval = DAILY;
      } else if (
        selectedDateRange === LAST_MONTH ||
        selectedDateRange === THIS_MONTH ||
        selectedDateRange === LAST_30_DAYS ||
        selectedDateRange === LAST_90_DAYS
      ) {
        this.selectedGraphInterval = WEEKLY;
      } else {
        this.selectedGraphInterval = MONTHLY;
      }
    },
    defineGraphIntervalItems(dateRange) {
      if (
        dateRange === CURRENT_DAY ||
        dateRange === YESTERDAY ||
        dateRange === THIS_WEEK ||
        dateRange === LAST_7_DAYS ||
        dateRange === LAST_WEEK ||
        dateRange === LAST_14_DAYS
      ) {
        this.graphIntervals = [{ interval: HOURLY }, { interval: DAILY }];
      } else if (dateRange === LAST_MONTH || dateRange === THIS_MONTH) {
        this.graphIntervals = [{ interval: HOURLY }, { interval: DAILY }, { interval: WEEKLY }];
      } else if (
        dateRange === LAST_30_DAYS ||
        dateRange === LAST_90_DAYS ||
        dateRange === THIS_YEAR
      ) {
        this.graphIntervals = [{ interval: DAILY }, { interval: WEEKLY }, { interval: MONTHLY }];
      } else {
        this.graphIntervals = [{ interval: MONTHLY }];
      }
    },
    selectedItemEventsDropdownLeft(value) {
      if (!value) {
        return;
      }
      this.selectedDropdownLeft = value.text;
      this.selectedDropdownLeftColor = value.graph_color;
      this.graphLeftDropdown = value.value;
      this.setEventsHeadersDropdownRight();
    },
    selectedItemEventsDropdownRight(selectedItem) {
      if (!selectedItem) {
        return;
      }

      this.selectedDropdownRight = selectedItem.text;
      this.selectedDropdownRightColor = classes.rightColorIcon;
      this.graphRightDropdown = selectedItem.value;
      this.eventsHeadersDropdownLeft = this.headers.filter(
        header => header.value !== TEAM_MEMBER && header.text !== this.selectedDropdownRight,
      );
    },
    setEventsHeadersDropdownRight() {
      this.eventsHeadersDropdownRight = this.headers.filter(
        header => header.value !== TEAM_MEMBER && header.text !== this.selectedDropdownLeft,
      );
    },
    clearSelectedEventDropdownRight() {
      this.selectedDropdownRight = SELECT;
      this.selectedDropdownRightColor = classes.hideColorIcon;
      this.graphRightDropdown = SELECT;
      this.eventsHeadersDropdownLeft = this.headers.filter(
        header => header.value !== TEAM_MEMBER && header.text !== this.selectedDropdownRight,
      );
    },
    showComparisonLeftIcon() {
      return [
        !this.includeComparedPercentage ? classes.hideComparisonIcon : classes.showComparisonIcon,
      ];
    },
    showComparisonRightIcon() {
      if (this.selectedDropdownRight === SELECT) return classes.hideComparisonIcon;
      return [
        !this.includeComparedPercentage ? classes.hideComparisonIcon : classes.showComparisonIcon,
      ];
    },
  },
  mounted() {
    this.$root.$on('click:filter', value => {
      this.filterSelection = value;
    });
  },
};
</script>

<style lang="scss" scoped>
.main-card {
  margin-bottom: 10px;
  padding: 20px 20px 20px 20px;
}
.events-dropdown-list {
  padding: 10px 15px 0 15px;
}
.dropdown-versus {
  display: inline-block;
  margin: 0 12px;
  font-weight: 600;
  color: var(--v-text-lighten4);
}
.interval-dropdown-title {
  font-weight: 500;
  margin-left: 20px;
  border: none;
  border-bottom: 1px solid var(--v-primary-base);
}
.left-color-icon {
  color: #005ea2;
}
.right-color-icon {
  color: #f7883e;
}
.hide-color-icon {
  display: none;
}
.dropdown-clear {
  border-top: 1px solid var(--v-background-lighten3);
  margin-top: 12px;
}
.dropdown-clear-span {
  color: var(--v-secondary-lighten4);
  font-weight: bold;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}
.dropdown-clear-icon {
  margin-right: 12px;
  height: 12px;
  width: 12px;
  color: var(--v-secondary-lighten4);
}
.placeholder-graph {
  background-color: var(--v-background-lighten4);
  margin-top: 20px;
  height: 300px;
}
.no-data-container {
  min-height: 400px;
}
.no-data-text {
  color: var(--v-text-lighten4);
  font-size: 16px;
}
.no-data-icon {
  font-size: 36px;
  margin-bottom: 12px;
  color: var(--v-secondary-lighten4);
}
.show-comparison-icon {
  display: inline-block;
  margin-left: -7px;
}
.hide-comparison-icon {
  display: none;
}
</style>
